import React, { useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useData } from '../../hooks/useData';
import { useApp } from '../../contexts/AppContext';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../utils/errorLogging';
import { TABLES } from '../../constants';
import { MODAL_TYPES } from '../../constants/modalTypes';
import BOQTable from './BOQTable';
import BOQExport from './BOQExport';
import BOQSetup from './Setup/BOQSetup';
import BOQQuotesTab from './BOQQuotesTab';
import BOQNotesTab from './BOQNotesTab';
import styles from './BOQPage.module.css';
import pageStyles from '../../styles/pages.module.css';
import PageToolbar from '../shared/PageToolbar';

const BOQPage = () => {
  const { currentProject, openModal } = useApp();
  const { logError } = useErrorLogging();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = window.matchMedia('(max-width:768px)').matches;

  // Fetch cost types
  const {
    data: costTypes,
    isLoading: isLoadingCostTypes,
    error: costTypesError,
  } = useData(TABLES.REFERENCE_COST_TYPES, {
    onSuccess: () => {},
    select: '*',
    enabled: true,
    realtime: false,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Failed to fetch cost types',
      });
    },
  });

  // Fetch project items — left join to master_items so custom items (master_item_id=null) also appear
  const {
    data: allProjectItems,
    isLoading: isLoadingProjectItems,
    error: projectItemsError,
  } = useData(TABLES.PROJECT_ITEMS, {
    select: `
      id,
      master_item_id,
      name_override,
      bb_chapter_id,
      space_id,
      quantity,
      estimated_cost,
      cost_override,
      total_cost,
      selected_option,
      details,
      relationships,
      master_items (
        id,
        item_name,
        include_in_boq,
        bb_chapter_id,
        cost_type_id
      )
    `,
    filter: { project_id: currentProject?.id, deleted_at: null },
    enabled: Boolean(currentProject?.id),
    realtime: true,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Failed to fetch project items for BOQ',
        metadata: { projectId: currentProject?.id },
      });
    },
  });

  // Fetch project vendors with master vendor names for display
  const {
    data: projectVendors,
    isLoading: isLoadingVendors,
    error: vendorsError,
  } = useData(TABLES.PROJECT_VENDORS, {
    select: `
      id,
      project_item_id,
      master_vendors (
        id,
        name
      )
    `,
    enabled: Boolean(currentProject?.id),
    realtime: false,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Failed to fetch project vendors',
      });
    },
  });

  // Filter to BOQ-relevant items: include_in_boq=true OR custom items (no master_item, has bb_chapter_id)
  // Exclude parent bundle wrappers
  const projectItemRows = useMemo(() => {
    if (!allProjectItems) return [];
    return allProjectItems.filter(pi => {
      // Exclude parent bundle wrappers
      if (pi.relationships?.bundle?.role === 'parent') return false;
      // Include if master_item says include_in_boq
      if (pi.master_items?.include_in_boq === true) return true;
      // Include custom items (no master_item, has bb_chapter_id)
      if (!pi.master_item_id && pi.bb_chapter_id) return true;
      return false;
    });
  }, [allProjectItems]);

  // Fetch project spaces for space name display in tooltips
  const {
    data: projectSpaces,
    isLoading: isLoadingSpaces,
    error: spacesError,
  } = useData(TABLES.PROJECT_SPACES, {
    select: 'id, custom_space_name',
    filter: { deleted_at: null },
    enabled: Boolean(currentProject?.id),
    realtime: false,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Failed to fetch project spaces',
      });
    },
  });

  // Build spaceMap: space_id -> name for tooltip display
  const spaceMap = useMemo(() => {
    const map = new Map();
    if (!projectSpaces) return map;
    projectSpaces.forEach(sp => map.set(sp.id, sp.custom_space_name || 'חלל'));
    return map;
  }, [projectSpaces]);

  // Build vendorMap: project_item_id -> { name } for display in the table
  const vendorMap = useMemo(() => {
    const map = new Map();
    if (!projectVendors) return map;
    projectVendors.forEach(pv => {
      if (pv.project_item_id && pv.master_vendors?.name) {
        map.set(pv.project_item_id, { name: pv.master_vendors.name });
      }
    });
    return map;
  }, [projectVendors]);

  // Fetch Blue Book chapters for table grouping
  const {
    data: bbChapters,
    isLoading: isLoadingBbChapters,
    error: bbChaptersError,
  } = useData(TABLES.REFERENCE_BB_CHAPTERS, {
    select: 'id, parent_id, code, name_he, depth, display_order',
    enabled: true,
    realtime: false,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Failed to fetch BB chapters',
      });
    },
  });

  const isLoading =
    isLoadingCostTypes ||
    isLoadingBbChapters ||
    isLoadingProjectItems ||
    isLoadingVendors ||
    isLoadingSpaces;
  const error =
    costTypesError || bbChaptersError || projectItemsError || vendorsError || spacesError;

  // Handle template import
  if (error) {
    return <div className={styles.errorMessage}>Error loading BOQ data: {error.message}</div>;
  }

  const tabs = [
    { path: '/boq/items', label: 'כתב כמויות' },
    { path: '/boq/quotes', label: 'הצעות מחיר' },
    { path: '/boq/notes', label: 'הערות' },
  ];

  // Default to items tab if no match found
  const currentTab =
    tabs.find(tab => location.pathname.includes(tab.path.split('/').pop())) || tabs[0];

  const handleTabChange = path => {
    navigate(path);
  };

  if (!currentProject) {
    return (
      <div className={pageStyles.card}>
        <div className={pageStyles.cardContent}>
          <h2>אנא בחר פרויקט</h2>
        </div>
      </div>
    );
  }

  // Convert tabs for PageToolbar format
  const toolbarTabs = tabs.map(tab => ({
    id: tab.path,
    label: tab.label,
    to: tab.path,
  }));

  return (
    <div className={pageStyles.pageContainer}>
      {isMobile ? (
        <select
          value={currentTab.path}
          onChange={e => handleTabChange(e.target.value)}
          className={styles.mobileSelect}
        >
          {tabs.map(tab => (
            <option key={tab.path} value={tab.path}>
              {tab.label}
            </option>
          ))}
        </select>
      ) : (
        <PageToolbar tabs={toolbarTabs} />
      )}
      <Routes>
        <Route path="/" element={<Navigate to="items" replace />} />
        <Route
          path="items"
          element={
            <>
              <div className={pageStyles.tableActions}>
                <div className={pageStyles.tableActionsRight}>
                  <button
                    className={pageStyles.primaryButton}
                    onClick={() =>
                      openModal(MODAL_TYPES.BOQ_FORM, {
                        bbChapters,
                        costTypes,
                        projectId: currentProject?.id,
                        onSuccess: () => {
                          // React Query will refetch via realtime subscription on project_items
                        },
                      })
                    }
                  >
                    הוסף פריט
                  </button>
                </div>
                <div className={pageStyles.tableActionsLeft}>
                  <BOQExport
                    projectItemRows={projectItemRows}
                    currentProject={currentProject}
                    costTypes={costTypes}
                    bbChapters={bbChapters}
                  />
                </div>
              </div>
              <BOQTable
                projectItemRows={projectItemRows}
                costTypes={costTypes}
                bbChapters={bbChapters}
                vendorMap={vendorMap}
                spaceMap={spaceMap}
                isLoading={isLoading}
              />
            </>
          }
        />
        <Route
          path="quotes"
          element={
            <BOQQuotesTab
              projectItemRows={projectItemRows}
              bbChapters={bbChapters}
              costTypes={costTypes}
            />
          }
        />
        <Route
          path="notes"
          element={<BOQNotesTab bbChapters={bbChapters} projectId={currentProject?.id} />}
        />
        <Route path="setup" element={<BOQSetup />} />
        <Route path="*" element={<Navigate to="items" replace />} />
      </Routes>
    </div>
  );
};

export default BOQPage;
